import { useEffect, useMemo, useRef, useState } from "react";
import { api, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Layers, Plus, X, Pencil, Trash2, PackagePlus, History, AlertTriangle,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";
import InputRupiah from "@/components/InputRupiah";

/** Resep Bundling (Inventory #28) — PAKET JUAL dari beberapa produk (mis.
 * "Paket Hemat" = 1x HP + 1x Casing + 1x Anti Gores) dengan harga paket yang
 * lebih murah daripada beli satuan. MODEL 23 Agu 2026 (brief bos poin 8-11):
 * paket = CARA MENJUAL, bukan barang — tidak ada SKU fisik "paket" yang berstok
 * sendiri. Stok komponen berkurang OTOMATIS saat paket dijual lewat POS/nota
 * (routers/sales.py, nota_module.py); halaman ini HANYA mengelola resepnya.
 *
 * Brief owner 22 Sep 2026 (bagian F, keputusan F1/F2):
 *  - Komponen ditaruh PALING DEPAN di form — resep memang boleh berisi banyak
 *    produk sejak dulu, tapi form yang diawali "Produk jadi" membuatnya tampak
 *    seperti satu produk saja ("fitur ada tapi tak terlihat", CLAUDE.md).
 *  - "Harga Paket" di form MENULIS harga jual produk paket (satu angka yang
 *    juga dipakai Master Produk, Price List, dan kasir). Isiannya hanya tampil
 *    bagi pemegang `inventory_produk.create` — izin yang SAMA dengan ubah harga
 *    di Master Produk; server menegakkannya juga di PUT (yang sendiri digerbangi
 *    `.update`).
 *  - Produk paket bisa DIBUAT dari form ini (kategori non-gadget) — dulu
 *    produknya wajib sudah aktif & berharga di Master Produk lebih dulu.
 *  - Total harga satuan komponen + "hemat Rp X" ditampilkan; paket yang TIDAK
 *    lebih murah hanya diperingatkan, tidak diblokir.
 *
 * KONTRAK BACKEND (routers/inventory/bundling.py):
 *  - CRUD resep memakai izin `inventory_produk` (SENGAJA tidak ada module key
 *    baru), jadi gating di halaman ini memakai can("inventory_produk", aksi).
 *  - Resep BOLEH memuat komponen ber-IMEI — IMEI unit fisik dipilih SAAT INPUT
 *    NOTA, bukan di sini.
 *  - Stok kosong saat resep dibuat/diedit HANYA peringatan (poin 9).
 *  - Update resep TIDAK BOLEH mengganti sku_bundle (400), dan ditolak kalau
 *    takaran komponennya berubah padahal resepnya sudah pernah dipakai menjual.
 */

const inp = "w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

export default function Bundling() {
  const { can } = useAuth();
  const bolehLihat = can("inventory_produk", "view");
  const bolehBuat = can("inventory_produk", "create");
  const bolehUbah = can("inventory_produk", "update");
  const bolehHapus = can("inventory_produk", "delete");
  // Harga paket = harga jual produk -> izin ubah harga Master Produk (`create`),
  // BUKAN izin ubah resep (`update`). Server menolak 403 dengan kunci yang sama.
  const bolehHarga = bolehBuat;
  const [resep, setResep] = useState([]);
  const [loadingResep, setLoadingResep] = useState(true);
  // Riwayat rakit/urai FISIK (model LAMA) — endpoint dipertahankan backend
  // untuk histori pra-23 Agu 2026; sejak model paket virtual, tabel ini tidak
  // pernah bertambah lagi (lihat pesan retired di bagian (c) di bawah).
  const [riwayat, setRiwayat] = useState([]);
  const [loadingRiwayat, setLoadingRiwayat] = useState(true);

  const [form, setForm] = useState(null); // modal tambah/edit resep
  const [busyForm, setBusyForm] = useState(false);

  // Cek stok virtual (poin 9/11) — "stok paket" DITURUNKAN dari stok
  // komponen (routers/inventory/bundling.py::stok_virtual_bundle), bukan
  // angka tersendiri. Dipakai untuk pratinjau saja; penolakan KERAS beneran
  // terjadi di routers/sales.py/nota_module.py saat paket ini benar-benar
  // dijual.
  const [cekBundleId, setCekBundleId] = useState("");
  const [stokDetail, setStokDetail] = useState(null);
  const [loadingStok, setLoadingStok] = useState(false);
  // Pengurutan tabel (client-side, lihat components/ui/sort.jsx) — urutan
  // bawaan tetap seperti sebelumnya.
  const resepSort = useSortState(null, "asc");
  const riwayatSort = useSortState(null, "asc");

  const muatResep = async () => {
    if (!bolehLihat) { setLoadingResep(false); return; }
    setLoadingResep(true);
    try {
      const r = await api.get("/inventory/bundling/definisi", { params: { include_inactive: true } });
      setResep(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat resep bundling");
    } finally { setLoadingResep(false); }
  };

  const muatRiwayat = async () => {
    if (!bolehLihat) { setLoadingRiwayat(false); return; }
    setLoadingRiwayat(true);
    try {
      const r = await api.get("/inventory/bundling");
      setRiwayat(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat riwayat rakit/urai");
    } finally { setLoadingRiwayat(false); }
  };

  useEffect(() => {
    muatResep();
    muatRiwayat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bolehLihat]);

  useEffect(() => {
    if (!cekBundleId) { setStokDetail(null); return; }
    let batal = false;
    setLoadingStok(true);
    (async () => {
      try {
        const r = await api.get(`/inventory/bundling/definisi/${cekBundleId}/stok`);
        if (!batal) setStokDetail(r.data);
      } catch (e) {
        if (!batal) toast.error(e?.response?.data?.detail || "Gagal memuat stok virtual paket");
      } finally {
        if (!batal) setLoadingStok(false);
      }
    })();
    return () => { batal = true; };
  }, [cekBundleId]);

  const resepUntukCek = useMemo(
    () => resep.find(r => r.id === cekBundleId) || null,
    [resep, cekBundleId],
  );

  const bukaTambah = () => setForm({
    id: null, nama: "",
    komponen: [{ sku: "", nama: "", qty: 1, harga_jual: 0 }],
    // Produk paket BARU dari form ini jadi bawaan — alasan fitur ini dibuat
    // (brief 22 Sep F): paket tidak perlu lagi disiapkan di Master Produk dulu.
    mode_produk: "baru",
    sku_bundle: "", nama_produk_jadi: "",
    produk_baru: { sku: "", nama: "", kategori: "accessories", brand: "", satuan: "pcs" },
    harga_paket: "",
    is_active: true,
  });

  const bukaEdit = (r) => setForm({
    id: r.id, nama: r.nama,
    komponen: (r.komponen || []).map(k => ({
      sku: k.sku, nama: k.nama || k.sku, qty: k.qty, harga_jual: Number(k.harga_jual) || 0,
    })),
    mode_produk: "ada",
    sku_bundle: r.sku_bundle, nama_produk_jadi: r.nama_bundle || r.sku_bundle,
    produk_baru: null,
    harga_paket: r.harga_paket ?? "",
    harga_paket_awal: r.harga_paket ?? null,
    is_active: !!r.is_active,
  });

  const simpanResep = async () => {
    if (!form.nama.trim()) return toast.error("Nama resep wajib diisi");
    const komponenValid = form.komponen.filter(k => k.sku);
    if (komponenValid.length === 0) return toast.error("Resep butuh minimal 1 komponen");
    if (komponenValid.some(k => !(Number(k.qty) > 0))) return toast.error("Qty tiap komponen harus lebih dari 0");
    const buatProduk = !form.id && form.mode_produk === "baru";
    if (buatProduk) {
      const pb = form.produk_baru || {};
      if (!bolehHarga) return toast.error("Membuat produk paket butuh izin ubah harga Master Produk");
      if (!(pb.sku || "").trim()) return toast.error("SKU produk paket wajib diisi");
      // Nama produk kosong = ikut nama resep (placeholder yang terlihat di form).
      if (!(pb.nama || form.nama || "").trim()) return toast.error("Nama produk paket wajib diisi");
      if (!(pb.brand || "").trim()) return toast.error("Brand produk paket wajib diisi");
      if (!(Number(form.harga_paket) > 0)) return toast.error("Harga paket wajib diisi untuk produk paket baru");
    } else if (!form.sku_bundle) {
      return toast.error("Pilih produk paket dari daftar pencarian dulu");
    }
    if (bolehHarga && form.harga_paket !== "" && form.harga_paket != null && !(Number(form.harga_paket) > 0)) {
      return toast.error("Harga paket harus lebih dari Rp 0");
    }
    setBusyForm(true);
    try {
      const payload = {
        sku_bundle: buatProduk ? "" : form.sku_bundle,
        nama: form.nama.trim(),
        komponen: komponenValid.map(k => ({ sku: k.sku, qty: Number(k.qty) || 0 })),
        is_active: !!form.is_active,
      };
      // Harga paket hanya dikirim oleh pemegang izin harga — akun ubah-resep
      // saja tidak pernah mengirimnya, jadi simpan resep tidak berujung 403.
      // Dan hanya kalau DIUBAH di form ini: harga di form berasal dari daftar
      // yang bisa sudah basi, dan mengirim ulang angka yang tidak disentuh
      // mengembalikan harga yang baru diubah orang lain lewat Master Produk.
      // `harga_paket_lama` = angka yang dilihat saat form dibuka; server
      // menolak 409 kalau harga di basis data sudah berbeda.
      const hargaBaru = Number(form.harga_paket);
      const adaAwal = form.harga_paket_awal !== undefined && form.harga_paket_awal !== null;
      const hargaDiubah = buatProduk || !adaAwal || hargaBaru !== Number(form.harga_paket_awal);
      if (bolehHarga && hargaBaru > 0 && hargaDiubah) {
        payload.harga_paket = hargaBaru;
        if (!buatProduk && adaAwal) payload.harga_paket_lama = Number(form.harga_paket_awal);
      }
      if (buatProduk) {
        const pb = form.produk_baru;
        payload.produk_baru = {
          sku: pb.sku.trim().toUpperCase(), nama: (pb.nama || form.nama).trim(), kategori: pb.kategori,
          brand: pb.brand.trim(), satuan: pb.satuan || "pcs",
        };
      }
      let res;
      if (form.id) {
        res = await api.put(`/inventory/bundling/definisi/${form.id}`, payload);
      } else {
        res = await api.post("/inventory/bundling/definisi", payload);
      }
      // Peringatan stok kosong (poin 9) & harga tidak lebih murah (F1) — TIDAK
      // memblokir simpan, cuma ditampilkan terpisah dari toast sukses biasa.
      const peringatan = res.data.peringatan_stok_kosong || [];
      toast.success(res.data.message || "Resep bundling tersimpan");
      if (peringatan.length > 0) {
        toast.warning(
          `Peringatan: ${peringatan.length} komponen sedang kosong stok (${peringatan.map(p => p.nama || p.sku).join(", ")}) — resep tetap tersimpan.`,
          { duration: 8000 },
        );
      }
      for (const teks of (res.data.peringatan_harga || [])) {
        toast.warning(teks, { duration: 9000 });
      }
      setForm(null);
      await muatResep();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan resep bundling");
    } finally { setBusyForm(false); }
  };

  const nonaktifkanResep = async (r) => {
    if (!window.confirm(
      `Nonaktifkan resep '${r.nama}'?\n\nResep ini tidak akan bisa dipakai menjual paket baru, tapi ` +
      "riwayat penjualan lama yang memakai resep ini tetap utuh."
    )) return;
    try {
      const res = await api.delete(`/inventory/bundling/definisi/${r.id}`);
      toast.success(res.data.message || "Resep bundling dinonaktifkan");
      await muatResep();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menonaktifkan resep bundling");
    }
  };

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8" data-testid="bundling">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          Akun Anda belum diberi izin melihat Resep Bundling. Minta admin mencentang modul
          "Inventory · Master Produk" pada role Anda.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="bundling">
      <div>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Inventory</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Resep Bundling</h1>
        <p className="text-sm text-slate-600 mt-1">
          Jual beberapa produk sebagai satu paket dengan harga lebih murah daripada beli satuan. Paket
          adalah CARA MENJUAL, bukan barang tersendiri — stok komponennya otomatis berkurang saat paket
          dijual di POS/nota.
        </p>
      </div>

      {/* ===== (a) Resep Bundling ===== */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-slate-500" />
            <h2 className="font-display font-bold text-slate-900 text-sm">Daftar Resep</h2>
          </div>
          {bolehBuat && (
            <button onClick={bukaTambah} data-testid="bd-resep-tambah"
                    className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Resep Baru
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          {loadingResep ? (
            <LoadingState loading data-testid="bd-resep-loading" />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr className="text-left">
                  <SortTh label="Produk Paket" sortKeyName="nama_bundle" sortKey={resepSort.sortKey} sortDir={resepSort.sortDir} onSort={resepSort.toggle} testid="bd-resep-sort-produk" />
                  <SortTh label="Nama Resep" sortKeyName="nama" sortKey={resepSort.sortKey} sortDir={resepSort.sortDir} onSort={resepSort.toggle} testid="bd-resep-sort-nama" />
                  <th className="px-3 py-2.5 font-semibold">Komponen</th>
                  <SortTh label="Harga Paket" sortKeyName="harga_paket" sortKey={resepSort.sortKey} sortDir={resepSort.sortDir} onSort={resepSort.toggle} align="right" testid="bd-resep-sort-harga" />
                  <th className="px-3 py-2.5 font-semibold text-right">Total Satuan</th>
                  <SortTh label="Hemat" sortKeyName="hemat" sortKey={resepSort.sortKey} sortDir={resepSort.sortDir} onSort={resepSort.toggle} align="right" testid="bd-resep-sort-hemat" />
                  <th className="px-3 py-2.5 font-semibold text-right">Stok Virtual</th>
                  <th className="px-3 py-2.5 font-semibold">Status</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {resep.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Belum ada resep bundling</td></tr>
                ) : sortRows(resep, resepSort.sortKey, resepSort.sortDir).map(r => (
                  <tr key={r.id} data-testid={`bd-resep-row-${r.id}`} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2.5">
                      <p className="text-slate-800">{r.nama_bundle}</p>
                      <p className="font-mono text-[11px] text-slate-500">{r.sku_bundle}</p>
                    </td>
                    <td className="px-3 py-2.5 text-slate-800">{r.nama}</td>
                    <td className="px-3 py-2.5 text-slate-600 text-xs">
                      {(r.komponen || []).map(k => `${k.qty}× ${k.nama || k.sku}`).join(", ")}
                      {(r.peringatan_stok_kosong || []).length > 0 && (
                        <span className="block mt-1 text-[10px] text-amber-700 inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> {r.peringatan_stok_kosong.length} komponen kosong stok
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-900" data-testid={`bd-resep-harga-${r.id}`}>
                      {r.harga_paket ? idr(r.harga_paket) : "-"}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-600">
                      {r.total_harga_satuan ? idr(r.total_harga_satuan) : "-"}
                    </td>
                    <td className="px-3 py-2.5 text-right" data-testid={`bd-resep-hemat-${r.id}`}>
                      <SelHemat r={r} />
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono" data-testid={`bd-resep-stok-virtual-${r.id}`}>
                      {r.stok_virtual ? r.stok_virtual.qty : "-"}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[11px] px-2 py-0.5 rounded border ${r.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                        {r.is_active ? "Aktif" : "Nonaktif"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {bolehUbah && (
                          <button onClick={() => bukaEdit(r)} data-testid={`bd-resep-edit-${r.id}`}
                                  title="Ubah resep" className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center">
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {bolehHapus && r.is_active && (
                          <button onClick={() => nonaktifkanResep(r)} data-testid={`bd-resep-nonaktifkan-${r.id}`}
                                  title="Nonaktifkan resep" className="w-8 h-8 rounded hover:bg-rose-50 text-rose-600 grid place-items-center">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ===== (b) Cek Stok Virtual Paket ===== */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2">
          <PackagePlus className="w-4 h-4 text-slate-500" />
          <h2 className="font-display font-bold text-slate-900 text-sm">Cek Stok Virtual Paket</h2>
        </div>
        <p className="text-xs text-slate-500">
          "Stok paket" bukan angka tersendiri — dihitung langsung dari stok komponen yang tersedia
          sekarang (MIN dari stok tiap komponen ÷ kebutuhan resep). Rakit/urai manual sudah tidak
          diperlukan lagi: stok komponen berkurang otomatis saat paket ini dijual di POS/nota.
        </p>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Resep</label>
          <select value={cekBundleId} data-testid="bd-cekstok-resep"
                  onChange={e => setCekBundleId(e.target.value)} className={inp}>
            <option value="">— pilih resep —</option>
            {resep.map(r => (
              <option key={r.id} value={r.id}>
                {r.nama} ({r.sku_bundle}){!r.is_active ? " — nonaktif" : ""}
              </option>
            ))}
          </select>
        </div>
        {loadingStok ? (
          <LoadingState loading data-testid="bd-cekstok-loading" />
        ) : stokDetail && resepUntukCek ? (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm" data-testid="bd-cekstok-hasil">
            <p className="font-semibold text-slate-800">
              Stok paket tersedia: <span className="font-mono">{stokDetail.qty}</span>
              {stokDetail.pembatas && (
                <span className="text-xs text-slate-500 font-normal"> (dibatasi komponen {stokDetail.pembatas})</span>
              )}
            </p>
            <table className="w-full text-xs mt-2">
              <thead className="text-slate-500">
                <tr className="text-left">
                  <th className="py-1 pr-2 font-medium">Komponen</th>
                  <th className="py-1 pr-2 font-medium text-right">Qty/Paket</th>
                  <th className="py-1 pr-2 font-medium text-right">Stok Komponen</th>
                  <th className="py-1 font-medium text-right">Muat Berapa Paket</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {(stokDetail.rincian || []).map(r => (
                  <tr key={r.sku}>
                    <td className="py-1 pr-2">{r.nama || r.sku}</td>
                    <td className="py-1 pr-2 text-right font-mono">{r.qty_resep}</td>
                    <td className="py-1 pr-2 text-right font-mono">{r.stok_komponen}</td>
                    <td className="py-1 text-right font-mono">{r.tersedia_paket}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400">Pilih resep untuk melihat rincian stok komponennya.</p>
        )}
      </div>

      {/* ===== (c) Riwayat Rakit/Urai (model lama, pra-23 Agu 2026) ===== */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="bd-riwayat">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <h2 className="font-display font-bold text-slate-900 text-sm">Riwayat Rakit / Urai (lama)</h2>
        </div>
        <p className="px-4 pt-2 text-xs text-slate-500">
          Rakit/urai stok fisik manual sudah digantikan model paket virtual di atas — tabel ini
          hanya menampilkan histori sebelum 23 Agu 2026 dan tidak akan bertambah lagi.
        </p>
        <div className="overflow-x-auto">
          {loadingRiwayat ? (
            <LoadingState loading data-testid="bd-riwayat-loading" />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr className="text-left">
                  <SortTh label="Kode / Tanggal" sortKeyName="tanggal" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} testid="bd-riwayat-sort-tanggal" />
                  <SortTh label="Resep" sortKeyName="nama_bundle" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} testid="bd-riwayat-sort-resep" />
                  <SortTh label="Qty" sortKeyName="qty" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} align="right" testid="bd-riwayat-sort-qty" />
                  <th className="px-3 py-2.5 font-semibold">Arah</th>
                  <SortTh label="Modal Total" sortKeyName="total_modal" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} align="right" testid="bd-riwayat-sort-modal" />
                  <th className="px-3 py-2.5 font-semibold">Gudang</th>
                  <th className="px-3 py-2.5 font-semibold">Pembuat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {riwayat.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Belum ada riwayat rakit/urai</td></tr>
                ) : sortRows(riwayat, riwayatSort.sortKey, riwayatSort.sortDir).map(row => (
                  <tr key={row.id} data-testid={`bd-riwayat-row-${row.id}`} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2.5">
                      <p className="font-mono text-[11px] text-slate-500">{row.code}</p>
                      <p className="text-slate-800">{row.tanggal}</p>
                    </td>
                    <td className="px-3 py-2.5 text-slate-800">
                      {row.nama_bundle}
                      <span className="block font-mono text-[11px] text-slate-400">{row.sku_bundle}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-slate-700">{row.qty}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[11px] px-2 py-0.5 rounded border ${row.tipe === "rakit" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                        {row.tipe === "rakit" ? "Rakit" : "Urai"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-900">{idr(row.total_modal)}</td>
                    <td className="px-3 py-2.5 text-slate-600">{row.warehouse_nama}</td>
                    <td className="px-3 py-2.5 text-slate-600">{row.created_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {form && (
        <FormResep form={form} setForm={setForm} busy={busyForm} onSimpan={simpanResep}
                   mengedit={!!form.id} bolehHarga={bolehHarga} />
      )}
    </div>
  );
}

/** Sel "Hemat" di tabel resep: hijau = paket lebih murah, amber = tidak. */
function SelHemat({ r }) {
  if (!r.harga_paket || !r.total_harga_satuan) return <span className="text-slate-400">-</span>;
  if (r.hemat > 0) return <span className="font-mono text-emerald-700">{idr(r.hemat)}</span>;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-amber-700" title="Harga paket tidak lebih murah dari beli satuan">
      <AlertTriangle className="w-3 h-3" /> tidak lebih murah
    </span>
  );
}

/** Autocomplete satu baris komponen resep — pola sama dengan `BarisItem` di
 * InventoryPO.jsx / `ItemNamaField` di Sales.jsx. Barang ber-IMEI BOLEH
 * dipilih (poin 8, brief bos 23 Agu 2026) — ditandai badge info saja, karena
 * IMEI unit fisik yang benar-benar dipakai dipilih staf SAAT INPUT NOTA. Harga
 * jual satuan ikut dicatat supaya form bisa menjumlah "total harga satuan". */
function KomponenRow({ idx, item, onUbah, onHapus, bisaHapus }) {
  const [saran, setSaran] = useState([]);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const cari = (teks) => {
    onUbah(idx, { nama: teks, sku: "", harga_jual: 0 });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 2) { setSaran([]); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      try {
        const { data } = await api.get("/inventory/products/lookup", { params: { q } });
        if (seq === reqSeq.current) setSaran(data.items || []);
      } catch { if (seq === reqSeq.current) setSaran([]); }
    }, 300);
  };

  const pilih = (p) => {
    onUbah(idx, { sku: p.sku, nama: p.nama, harga_jual: Number(p.harga_jual) || 0 });
    setSaran([]);
  };

  const subtotal = (Number(item.harga_jual) || 0) * (Number(item.qty) || 0);
  return (
    <div className="flex items-start gap-2">
      <div className="flex-1 relative">
        <input value={item.nama} placeholder="Ketik SKU atau nama produk" onChange={e => cari(e.target.value)}
               data-testid={`bd-komponen-sku-${idx}`} className={inp} />
        {item.sku && (
          <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
            SKU {item.sku} · {idr(item.harga_jual || 0)}/satuan
          </span>
        )}
        {saran.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
            {saran.map(p => {
              const berImei = !!p.has_imei;
              return (
                <button key={p.sku} type="button"
                        onClick={() => pilih(p)}
                        title={berImei ? "Ber-IMEI — IMEI unit dipilih saat input nota, bukan di sini" : ""}
                        data-testid={`bd-komponen-saran-${idx}-${p.sku}`}
                        className="w-full text-left px-3 py-2 text-xs border-b border-slate-50 last:border-0 hover:bg-emerald-50">
                  <span className="font-medium text-slate-900">{p.nama}</span>
                  <span className="font-mono text-[10px] text-slate-400 ml-1.5">{p.sku}</span>
                  <span className="text-[10px] text-slate-500 ml-1.5">{idr(p.harga_jual || 0)}</span>
                  {berImei && <span className="text-[10px] text-sky-700 ml-1.5">ber-IMEI</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <input type="number" min="1" value={item.qty} placeholder="Qty"
             onChange={e => onUbah(idx, { qty: e.target.value })}
             data-testid={`bd-komponen-qty-${idx}`} className={`${inp} w-20`} />
      <div className="w-28 h-10 flex items-center justify-end text-xs font-mono text-slate-600 shrink-0"
           data-testid={`bd-komponen-subtotal-${idx}`}>
        {item.sku ? idr(subtotal) : "-"}
      </div>
      {bisaHapus && (
        <button type="button" onClick={() => onHapus(idx)} data-testid={`bd-komponen-hapus-${idx}`}
                className="w-10 h-10 rounded hover:bg-rose-50 text-rose-600 grid place-items-center shrink-0">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function FormResep({ form, setForm, busy, onSimpan, mengedit, bolehHarga }) {
  const [saranBundle, setSaranBundle] = useState([]);
  const [opsi, setOpsi] = useState(null);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // Pilihan kategori/brand/satuan produk paket BARU — dari registry Master
  // Produk (sumber yang sama dengan form produk), hanya dimuat kalau dipakai.
  const butuhOpsi = !mengedit && form.mode_produk === "baru";
  useEffect(() => {
    if (!butuhOpsi || opsi) return;
    let batal = false;
    api.get("/inventory/master-opsi")
      .then(r => { if (!batal) setOpsi(r.data); })
      .catch(() => { if (!batal) setOpsi({}); });
    return () => { batal = true; };
  }, [butuhOpsi, opsi]);

  const cariBundle = (teks) => {
    setForm(f => ({ ...f, nama_produk_jadi: teks, sku_bundle: "" }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 2) { setSaranBundle([]); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      try {
        const { data } = await api.get("/inventory/products/lookup", { params: { q } });
        if (seq === reqSeq.current) setSaranBundle(data.items || []);
      } catch { if (seq === reqSeq.current) setSaranBundle([]); }
    }, 300);
  };

  const pilihBundle = (p) => {
    // Harga paket terisi harga jual produk yang dipilih — satu angka yang sama.
    // `harga_paket_awal` = pembanding di server (409 kalau harganya berubah
    // sebelum resep disimpan).
    setForm(f => ({ ...f, sku_bundle: p.sku, nama_produk_jadi: p.nama,
                    harga_paket: Number(p.harga_jual) || "", harga_paket_awal: Number(p.harga_jual) || 0 }));
    setSaranBundle([]);
  };

  const ubahKomponen = (idx, patch) => setForm(f => ({
    ...f, komponen: f.komponen.map((k, i) => i === idx ? { ...k, ...patch } : k),
  }));
  const hapusKomponen = (idx) => setForm(f => ({ ...f, komponen: f.komponen.filter((_, i) => i !== idx) }));
  const tambahKomponen = () => setForm(f => ({ ...f, komponen: [...f.komponen, { sku: "", nama: "", qty: 1, harga_jual: 0 }] }));
  const ubahProdukBaru = (patch) => setForm(f => ({ ...f, produk_baru: { ...f.produk_baru, ...patch } }));

  const totalSatuan = form.komponen
    .filter(k => k.sku)
    .reduce((s, k) => s + (Number(k.harga_jual) || 0) * (Number(k.qty) || 0), 0);
  const hargaPaket = Number(form.harga_paket) || 0;
  const hemat = totalSatuan - hargaPaket;

  const kategoriPaket = (opsi?.kategori_utama || []).filter(k => !k.wajib_status);
  const brandPaket = (opsi?.brand_per_kategori || {})[form.produk_baru?.kategori] || [];
  const satuanPaket = opsi?.satuan?.length ? opsi.satuan : ["pcs"];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setForm(null)}>
      <div onClick={e => e.stopPropagation()} data-testid="bd-resep-modal"
           className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Layers className="w-5 h-5 text-emerald-600" /> {mengedit ? "Ubah Resep Bundling" : "Resep Bundling Baru"}
          </h3>
          <button onClick={() => setForm(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Nama resep</label>
            <input value={form.nama} onChange={e => setForm(f => ({ ...f, nama: e.target.value }))}
                   placeholder="mis. Paket Hemat iPhone + Casing" data-testid="bd-resep-nama" className={inp} />
          </div>

          {/* ===== 1. Isi paket — boleh banyak produk ===== */}
          <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-xs font-semibold text-slate-700">Isi paket — produk yang dijual bersama</p>
              <p className="text-[11px] text-slate-500">Qty · subtotal harga satuan</p>
            </div>
            {form.komponen.map((k, i) => (
              <KomponenRow key={i} idx={i} item={k} onUbah={ubahKomponen} onHapus={hapusKomponen}
                           bisaHapus={form.komponen.length > 1} />
            ))}
            <button type="button" onClick={tambahKomponen} data-testid="bd-komponen-tambah"
                    className="text-sm text-emerald-700 hover:underline">+ Tambah produk ke paket</button>
            <p className="text-[11px] text-slate-500">
              Produk ber-IMEI boleh masuk paket — unit/IMEI-nya dipilih kasir saat input nota. Stok kosong
              hanya peringatan, tidak menghalangi resep disimpan.
            </p>
          </div>

          {/* ===== 2. Produk paket (yang muncul di kasir & Price List) ===== */}
          <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/60">
            <p className="text-xs font-semibold text-slate-700">Produk paket — nama yang dijual di kasir</p>
            {!mengedit && (
              <div className="flex flex-wrap gap-3 text-sm">
                <label className={`inline-flex items-center gap-1.5 ${bolehHarga ? "" : "opacity-50"}`}>
                  <input type="radio" name="bd-mode-produk" checked={form.mode_produk === "baru"}
                         disabled={!bolehHarga} data-testid="bd-produk-mode-baru"
                         onChange={() => setForm(f => ({ ...f, mode_produk: "baru" }))} />
                  Buat produk paket baru
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input type="radio" name="bd-mode-produk" checked={form.mode_produk === "ada"}
                         data-testid="bd-produk-mode-ada"
                         onChange={() => setForm(f => ({ ...f, mode_produk: "ada" }))} />
                  Pakai produk yang sudah ada
                </label>
              </div>
            )}
            {!mengedit && form.mode_produk === "baru" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">SKU</label>
                  <input value={form.produk_baru.sku} data-testid="bd-produk-baru-sku"
                         onChange={e => ubahProdukBaru({ sku: e.target.value.toUpperCase() })}
                         placeholder="mis. PKT-HEMAT-01" className={`${inp} font-mono`} />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Nama produk paket</label>
                  <input value={form.produk_baru.nama} data-testid="bd-produk-baru-nama"
                         onChange={e => ubahProdukBaru({ nama: e.target.value })}
                         placeholder={form.nama || "mis. Paket Hemat iPhone + Casing"} className={inp} />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Kategori</label>
                  <select value={form.produk_baru.kategori} data-testid="bd-produk-baru-kategori"
                          onChange={e => ubahProdukBaru({ kategori: e.target.value, brand: "" })} className={inp}>
                    {(kategoriPaket.length ? kategoriPaket : [{ key: "accessories", label: "Accessories" }])
                      .map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Brand</label>
                  {brandPaket.length > 0 ? (
                    <select value={form.produk_baru.brand} data-testid="bd-produk-baru-brand"
                            onChange={e => ubahProdukBaru({ brand: e.target.value })} className={inp}>
                      <option value="">— pilih brand —</option>
                      {brandPaket.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  ) : (
                    <input value={form.produk_baru.brand} data-testid="bd-produk-baru-brand"
                           onChange={e => ubahProdukBaru({ brand: e.target.value })} className={inp} />
                  )}
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-1">Satuan</label>
                  <select value={form.produk_baru.satuan} data-testid="bd-produk-baru-satuan"
                          onChange={e => ubahProdukBaru({ satuan: e.target.value })} className={inp}>
                    {satuanPaket.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <p className="sm:col-span-2 text-[11px] text-slate-500">
                  Produk paket memakai kategori non-gadget (Handphone/Tab/Laptop/Wearable memaksa nama &
                  SKU otomatis). Pendapatan paket dicatat di kategori ini.
                </p>
              </div>
            ) : mengedit ? (
              <div className={`${inp} flex items-center bg-slate-50 text-slate-600`}>
                {form.nama_produk_jadi}
                <span className="font-mono text-xs text-slate-400 ml-1.5">({form.sku_bundle})</span>
              </div>
            ) : (
              <div className="relative">
                <input value={form.nama_produk_jadi} onChange={e => cariBundle(e.target.value)}
                       placeholder="Ketik SKU atau nama produk paket" data-testid="bd-resep-sku" className={inp} />
                {form.sku_bundle && (
                  <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
                    SKU {form.sku_bundle}
                  </span>
                )}
                {saranBundle.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                    {saranBundle.map(p => (
                      <button key={p.sku} type="button"
                              onClick={() => pilihBundle(p)}
                              data-testid={`bd-resep-saran-${p.sku}`}
                              className="w-full text-left px-3 py-2 text-xs border-b border-slate-50 last:border-0 hover:bg-emerald-50">
                        <span className="font-medium text-slate-900">{p.nama}</span>
                        <span className="font-mono text-[10px] text-slate-400 ml-1.5">{p.sku}</span>
                        <span className="text-[10px] text-slate-500 ml-1.5">{idr(p.harga_jual || 0)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ===== 3. Harga paket vs beli satuan ===== */}
          <div className="space-y-2" data-testid="bd-ringkasan-harga">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Harga paket</label>
                {bolehHarga ? (
                  <InputRupiah value={form.harga_paket} testid="bd-harga-paket"
                               onChange={v => setForm(f => ({ ...f, harga_paket: v }))} />
                ) : (
                  <p className="h-10 flex items-center text-sm font-mono text-slate-700" data-testid="bd-harga-paket-baca">
                    {hargaPaket ? idr(hargaPaket) : "-"}
                    <span className="ml-2 text-[11px] font-sans text-slate-400">diubah oleh pemegang izin harga Master Produk</span>
                  </p>
                )}
              </div>
              <div className="text-xs text-slate-600 space-y-0.5">
                <p>Total harga satuan: <b className="font-mono">{idr(totalSatuan)}</b></p>
                {hargaPaket > 0 && totalSatuan > 0 && hemat > 0 && (
                  <p className="text-emerald-700" data-testid="bd-hemat">Pelanggan hemat <b className="font-mono">{idr(hemat)}</b></p>
                )}
              </div>
            </div>
            {hargaPaket > 0 && totalSatuan > 0 && hemat <= 0 && (
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1.5"
                 data-testid="bd-peringatan-harga">
                <AlertTriangle className="w-3.5 h-3.5" />
                Harga paket tidak lebih murah daripada beli satuan — tetap bisa disimpan.
              </p>
            )}
            <p className="text-[11px] text-slate-500">
              Harga paket = harga jual produk paket di Master Produk, Price List, dan kasir (satu angka).
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_active} data-testid="bd-resep-aktif"
                   onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                   className="w-4 h-4 rounded border-slate-300" />
            Resep aktif (paket bisa dijual di kasir)
          </label>
        </div>
        <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={() => setForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
          <button onClick={onSimpan} disabled={busy} data-testid="bd-resep-modal-submit"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
            {busy ? "Menyimpan..." : "Simpan Resep"}
          </button>
        </div>
      </div>
    </div>
  );
}
